import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IGame, IGameEntry } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';

import GamesSliderView, { IGamesSliderViewData } from './GamesSliderView';
import { IRootState } from 'store/serverInterfaces';
import { getEntries, getEntriesIfNewGoals, setSelectedGameId } from 'store/actions/dreamteamActions';
import { IInitialState } from 'interfaces/gameInterfaces';
import { getEventsByTeam } from 'store/actions/sportsActions';
import config from 'theme/config';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import { ReactGACategories } from 'constants/appEnums';

export const gameSliderFunctions = {
  handleSliderChange: (token: string, dispatch: any, selectedGameId: number, entries: IGameEntry[] | undefined, index: number, games: IGame[] | undefined, isAuthenticated: () => boolean) => {
    if (!dreamteamUtilities.redirectIfNotAuthenticated(isAuthenticated, dispatch)) {
      if (games && index < games.length) {
        const gameId = games[index].gameId;
        if (selectedGameId !== gameId) {
          dreamteamUtilities.recordEventGA(ReactGACategories.nav, games[index].gameName);
          dispatch(setSelectedGameId(gameId, entries, token));
        }
      }
    }
  }
};

const GamesSlider = () => {
  const dispatch = useDispatch();
  const userContext = useContext(UserContext);
  const dreamteamReducer: IInitialState = useSelector((state: IRootState) => state.DreamteamReducer);
  const sportsReducer: IInitialState = useSelector((state: IRootState) => state.SportsReducer);
  const [useShadow, setUsedShadow] = useState<boolean>(false);

  useEffect(() => {
    const selectedGame = dreamteamReducer.games?.find((game: IGame) => game.gameId === dreamteamReducer.selectedGameId);

    // if we are on local testing or in test, we set the frequency to 5 secs, otherwise we use the config
    const frequency = window.location.href.includes('localhost') || window.location.href.includes('test') ? 5000 : config.settings.refreshEventsFrequency;

    const refreshEvents = setInterval(() => {
      if (dreamteamUtilities.redirectIfNotAuthenticated(userContext.user.isAuthenticated, dispatch) || !(selectedGame && dreamteamReducer.selectedEntry)) {
        return;
      }

      if (sportsReducer.events) {
        if (dreamteamUtilities.getEventSummary(sportsReducer.events).atLeastOneLiveGame) {
          dispatch(getEventsByTeam(dreamteamReducer.selectedEntry.team));

          if (userContext.user.token && dreamteamReducer.selectedGameId) {
            // We only refresh the entry if there are new goals
            dispatch(getEntriesIfNewGoals(userContext.user.token, dreamteamReducer.selectedGameId, dreamteamReducer.selectedEntry.totalPoints));
          }

          // This last condition is to refresh the entries if there are goals but not returns, it means the bet is not settle yet
        } else if (userContext.user.token && dreamteamReducer.selectedGameId && dreamteamReducer.selectedEntry.totalPoints > 0 && dreamteamReducer.selectedEntry.returns === 0) {
          dispatch(getEntries(userContext.user.token, dreamteamReducer.selectedGameId));
        }
      } else {
        dispatch(getEventsByTeam(dreamteamReducer.selectedEntry.team));
      }
    }, frequency);
    return () => {
      clearInterval(refreshEvents);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dreamteamReducer.selectedGameId, dreamteamReducer.selectedEntry, sportsReducer.events]);

  if (!dreamteamReducer.games) {
    return <></>;
  }

  const settings = {
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '12px',
    infinite: false,
    focusOnSelect: true,
    cssEase: 'ease-in-out',
    edgeFriction: 0,
    beforeChange: (current: number, next: number) => {
      const totalSlides = dreamteamReducer?.games?.length || 0;
      const nextIsLastSlide = totalSlides - 1 === next;
      if (!config.settings.bonusEnabled) setUsedShadow(!nextIsLastSlide);
      gameSliderFunctions.handleSliderChange(
        userContext.user.token!,
        dispatch,
        dreamteamReducer.selectedGameId!,
        dreamteamReducer.entries,
        next,
        dreamteamReducer.games,
        userContext.user.isAuthenticated
      );
    }
  };

  const currentWeekIndex = dreamteamReducer.games?.findIndex((game: IGame) => {
    return game.gameId === dreamteamReducer.currentGameId;
  });

  const slideIndex = dreamteamReducer.games?.findIndex((game: IGame) => {
    return game.gameId === dreamteamReducer.selectedGameId;
  });

  const GamesSliderData: IGamesSliderViewData = {
    slides: dreamteamReducer.games,
    settings,
    currentWeekIndex,
    slideIndex,
    useShadow,
    showInfoButton: !config.settings.bonusEnabled,
    showBonusButton: config.settings.bonusEnabled && slideIndex === currentWeekIndex
  };

  return <GamesSliderView data={GamesSliderData} />;
};

export default GamesSlider;
